import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, CheckCircle, Ban } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { Button } from '../ui/button';
import { useClinic } from '@/contexts/ClinicContext';
import { clinicAdminService } from '@/services/clinicAdminService';
import { ClinicAdmin } from '@/models/user.model';

interface DeactivateAdminDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  admin: ClinicAdmin | null;
  onSuccess: () => void;
}

const DeactivateAdminDialog: React.FC<DeactivateAdminDialogProps> = ({
  open,
  onOpenChange,
  admin,
  onSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { clinic } = useClinic();

  if (!admin) return null;

  const isActive = admin.status === 'active';
  const actionText = isActive ? 'Deactivate' : 'Activate';
  const actionDescription = isActive
    ? "This will deactivate the admin. They will no longer have admin privileges, but their data will be preserved."
    : 'This will activate the admin and grant them admin privileges within the clinic.';

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      await clinicAdminService.toggleAdminStatus(clinic?.id, admin.id || '');

      toast({
        title: isActive ? 'Admin Deactivated' : 'Admin Activated',
        description: `Admin has been ${isActive ? 'deactivated' : 'activated'} successfully.`,
        variant: 'success',
      });

      onSuccess();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error?.message || 'Failed to toggle admin status',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              isActive ? 'bg-red-50' : 'bg-brand-teal/10'
            }`}>
              {isActive ? (
                <Ban className="h-6 w-6 text-red-500" />
              ) : (
                <CheckCircle className="h-6 w-6 text-brand-teal" />
              )}
            </div>
            <div className="flex-1">
              <DialogTitle className="text-[1.05rem] font-semibold">
                {actionText} Admin
              </DialogTitle>
              <DialogDescription className="text-[0.8rem] text-slate-500 mt-0">
                Are you sure you want to {actionText.toLowerCase()} this admin?
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-2">
          <p className="text-sm text-slate-600">
            Are you sure you want to {actionText.toLowerCase()} <span className="font-semibold text-slate-900">{admin.first_name} {admin.last_name}</span>? {actionDescription}
          </p>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading}
            className={isActive 
              ? 'bg-red-500 hover:bg-red-600' 
              : 'bg-brand-teal hover:bg-brand-teal/90'
            }
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {isActive ? 'Deactivating...' : 'Activating...'}
              </>
            ) : (
              <>
                {isActive ? <Ban className="h-4 w-4 mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                {actionText}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeactivateAdminDialog;
